import React, { useState, useEffect, useRef, memo, useCallback } from 'react';
import { ThumbsUp, ThumbsDown, Plus, Trash2, Users, Download, ChevronDown } from 'lucide-react';
import { database } from './firebase';
import { ref, set, onValue, update } from 'firebase/database';

export default function CDMGrid() {
  const [userName, setUserName] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentView, setCurrentView] = useState('cdm');
  const [showAddRowDropdown, setShowAddRowDropdown] = useState(false);
  const [userVotes, setUserVotes] = useState({});
  const dropdownRef = useRef(null);
  const saveTimeoutRef = useRef({});
  const rowPositionsRef = useRef({});
  const isTypingRef = useRef(false);
  
  const [rows, setRows] = useState([]);
  const [criticalRows, setCriticalRows] = useState([]);
  const [differentialRows, setDifferentialRows] = useState([]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowAddRowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isLoggedIn && sessionId) {
      const sessionRef = ref(database, `sessions/${sessionId}`);
      const unsubscribe = onValue(sessionRef, (snapshot) => {
        const data = snapshot.val();
        if (isTypingRef.current) return;
        if (data && data.rows) {
          setRows(data.rows || []);
          setCriticalRows(data.criticalRows || []);
          setDifferentialRows(data.differentialRows || []);
          setUserVotes(data.userVotes || {});
        } else {
          const defaultRows = [
            { id: 1, order: '1', diagnoses: [], supporting: [], opposing: [] },
            { id: 2, order: '2', diagnoses: [], supporting: [], opposing: [] },
            { id: 3, order: '3', diagnoses: [], supporting: [], opposing: [] },
          ];
          const defaultCriticalRows = [
            { id: 4, order: 'Critical Dx 1', diagnoses: [], supporting: [], opposing: [], isCritical: true }
          ];
          const defaultDifferentialRows = [
            { id: 1, etiologicalClass: [], diseases: [], supporting: [], opposing: [] },
            { id: 2, etiologicalClass: [], diseases: [], supporting: [], opposing: [] },
            { id: 3, etiologicalClass: [], diseases: [], supporting: [], opposing: [] },
          ];
          setRows(defaultRows);
          setCriticalRows(defaultCriticalRows);
          setDifferentialRows(defaultDifferentialRows);
          set(sessionRef, {
            rows: defaultRows,
            criticalRows: defaultCriticalRows,
            differentialRows: defaultDifferentialRows,
            userVotes: {},
            lastUpdated: Date.now()
          });
        }
      });
      return () => unsubscribe();
    }
  }, [isLoggedIn, sessionId]);

  // Debounced persistence for bulk changes (kept as-is)
  useEffect(() => {
    if (isLoggedIn && sessionId && rows.length > 0) {
      if (saveTimeoutRef.current['rows']) clearTimeout(saveTimeoutRef.current['rows']);
      saveTimeoutRef.current['rows'] = setTimeout(() => {
        const sessionRef = ref(database, `sessions/${sessionId}`);
        update(sessionRef, { rows, userVotes, lastUpdated: Date.now() })
          .catch(error => console.error('Error updating rows:', error));
      }, 1000);
    }
    return () => { if (saveTimeoutRef.current['rows']) clearTimeout(saveTimeoutRef.current['rows']); };
  }, [rows, userVotes, isLoggedIn, sessionId]);

  useEffect(() => {
    if (isLoggedIn && sessionId && criticalRows.length > 0) {
      if (saveTimeoutRef.current['criticalRows']) clearTimeout(saveTimeoutRef.current['criticalRows']);
      saveTimeoutRef.current['criticalRows'] = setTimeout(() => {
        const sessionRef = ref(database, `sessions/${sessionId}`);
        update(sessionRef, { criticalRows, lastUpdated: Date.now() })
          .catch(error => console.error('Error updating critical rows:', error));
      }, 1000);
    }
    return () => { if (saveTimeoutRef.current['criticalRows']) clearTimeout(saveTimeoutRef.current['criticalRows']); };
  }, [criticalRows, isLoggedIn, sessionId]);

  useEffect(() => {
    if (isLoggedIn && sessionId && differentialRows.length > 0) {
      if (saveTimeoutRef.current['differentialRows']) clearTimeout(saveTimeoutRef.current['differentialRows']);
      saveTimeoutRef.current['differentialRows'] = setTimeout(() => {
        const sessionRef = ref(database, `sessions/${sessionId}`);
        update(sessionRef, { differentialRows, lastUpdated: Date.now() })
          .catch(error => console.error('Error updating differential rows:', error));
      }, 1000);
    }
    return () => { if (saveTimeoutRef.current['differentialRows']) clearTimeout(saveTimeoutRef.current['differentialRows']); };
  }, [differentialRows, isLoggedIn, sessionId]);

  const addRow = () => setRows([...rows, { id: Date.now(), order: String(rows.length + 1), diagnoses: [], supporting: [], opposing: [] }]);
  const addCriticalRow = () => setCriticalRows([...criticalRows, { id: Date.now(), order: `Critical Dx ${criticalRows.length + 1}`, diagnoses: [], supporting: [], opposing: [], isCritical: true }]);
  const addDifferentialRow = () => setDifferentialRows([...differentialRows, { id: Date.now(), etiologicalClass: [], diseases: [], supporting: [], opposing: [] }]);

  const handleFocusChange = (hasFocus) => { isTypingRef.current = hasFocus; };

  const addItem = (rowId, column, text, isCritical = false) => {
    if (!text.trim()) return;
    const newItem = { id: Date.now(), text, addedBy: userName, votes: 0, voters: {} };
    const updateFunc = (r) => r.id === rowId ? { ...r, [column]: [...(r[column] || []), newItem] } : r;
    if (isCritical) setCriticalRows(criticalRows.map(updateFunc));
    else setRows(rows.map(updateFunc));
  };

  const addDifferentialItem = (rowId, column, text) => {
    if (!text.trim()) return;
    const newItem = { id: Date.now(), text, addedBy: userName, votes: 0, voters: {} };
    setDifferentialRows(differentialRows.map(r => r.id === rowId ? { ...r, [column]: column === 'etiologicalClass' ? [newItem] : [...(r[column] || []), newItem] } : r));
  };

  // Immediate + cumulative voting (fixes lag & "can't click multiple")
  const voteItem = useCallback((rowId, column, itemId, value, isCritical = false) => {
    if (!userName) return;
    const voteKey = `${rowId}-${column}-${itemId}-${userName}`;
    const sessionRef = ref(database, `sessions/${sessionId}`);

    if (isCritical) {
      setCriticalRows(prev => {
        const next = prev.map(r => {
          if (r.id !== rowId) return r;
          return {
            ...r,
            [column]: (r[column] || []).map(item => {
              if (item.id !== itemId) return item;
              const voters = { ...(item.voters || {}) };
              const prevAmt = voters[userName] || 0;
              const nextAmt = prevAmt + value;
              voters[userName] = nextAmt;
              const total = (item.votes || 0) - prevAmt + nextAmt;
              return { ...item, votes: total, voters };
            })
          };
        });
        // write just this array immediately to avoid perceived lag
        update(sessionRef, { criticalRows: next, userVotes: { ...(userVotes || {}), [voteKey]: (userVotes?.[voteKey] || 0) + value }, lastUpdated: Date.now() }).catch(()=>{});
        return next;
      });
    } else {
      setRows(prev => {
        const next = prev.map(r => {
          if (r.id !== rowId) return r;
          return {
            ...r,
            [column]: (r[column] || []).map(item => {
              if (item.id !== itemId) return item;
              const voters = { ...(item.voters || {}) };
              const prevAmt = voters[userName] || 0;
              const nextAmt = prevAmt + value;
              voters[userName] = nextAmt;
              const total = (item.votes || 0) - prevAmt + nextAmt;
              return { ...item, votes: total, voters };
            })
          };
        });
        update(sessionRef, { rows: next, userVotes: { ...(userVotes || {}), [voteKey]: (userVotes?.[voteKey] || 0) + value }, lastUpdated: Date.now() }).catch(()=>{});
        return next;
      });
    }

    setUserVotes(prev => ({ ...prev, [voteKey]: (prev[voteKey] || 0) + value }));
  }, [sessionId, userName, userVotes]);

  const voteDifferentialItem = useCallback((rowId, column, itemId, value) => {
    if (!userName) return;
    const voteKey = `diff-${rowId}-${column}-${itemId}-${userName}`;
    const sessionRef = ref(database, `sessions/${sessionId}`);

    setDifferentialRows(prevRows => {
      const next = prevRows.map(r => {
        if (r.id !== rowId) return r;
        return {
          ...r,
          [column]: (r[column] || []).map(item => {
            if (item.id !== itemId) return item;
            const voters = { ...(item.voters || {}) };
            const prevAmt = voters[userName] || 0;
            const nextAmt = prevAmt + value;
            voters[userName] = nextAmt;
            const total = (item.votes || 0) - prevAmt + nextAmt;
            return { ...item, votes: total, voters };
          })
        };
      });
      update(sessionRef, { differentialRows: next, userVotes: { ...(userVotes || {}), [voteKey]: (userVotes?.[voteKey] || 0) + value }, lastUpdated: Date.now() }).catch(()=>{});
      return next;
    });

    setUserVotes(prev => ({ ...prev, [voteKey]: (prev[voteKey] || 0) + value }));
  }, [sessionId, userName, userVotes]);

  const deleteItem = (rowId, column, itemId, isCritical = false) => {
    const updateFunc = (r) => r.id === rowId ? { ...r, [column]: (r[column] || []).filter(item => item.id !== itemId) } : r;
    if (isCritical) setCriticalRows(criticalRows.map(updateFunc));
    else setRows(rows.map(updateFunc));
  };

  const deleteDifferentialItem = (rowId, column, itemId) => {
    setDifferentialRows(differentialRows.map(r => r.id === rowId ? { ...r, [column]: (r[column] || []).filter(item => item.id !== itemId) } : r));
  };

  const exportToPDF = () => {
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) { alert('Please allow pop-ups to export the PDF'); return; }
    let content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>CDM Grid Export - ${sessionId}</title>
        <style>
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 30px; background: #f8fafc; margin: 0; }
          .container { max-width: 1200px; margin: 0 auto; background: white; padding: 40px; border-radius: 16px; box-shadow: 0 10px 25px rgba(0,0,0,0.1); }
          h1 { text-align: center; color: #1e293b; margin-bottom: 8px; font-size: 32px; font-weight: 300; }
          h2 { text-align: center; color: #64748b; font-size: 16px; margin-bottom: 40px; font-weight: normal; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 30px; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 6px rgba(0,0,0,0.1); }
          th { background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%); color: white; padding: 16px; text-align: left; font-weight: 500; font-size: 14px; border: none; }
          th:first-child { background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 25%); }
          th:nth-child(2) { background: linear-gradient(135deg, #8b5cf6 0%, #ec4899 50%); }
          th:nth-child(3) { background: linear-gradient(135deg, #ec4899 0%, #f97316 100%); }
          th:last-child { background: linear-gradient(135deg, #f97316 0%, #ef4444 100%); }
          td { border: 1px solid #e2e8f0; padding: 16px; vertical-align: top; line-height: 1.5; }
          tr:nth-child(even) { background: #f8fafc; }
          tr:nth-child(odd) { background: white; }
          .diagnosis { font-weight: 600; color: #1e293b; margin-bottom: 8px; font-size: 15px; }
          .rank { font-weight: 700; color: #3b82f6; font-size: 18px; }
          .critical { background: #fef2f2 !important; border-left: 4px solid #ef4444; }
          .critical .rank { color: #dc2626; }
          .footer { text-align: center; margin-top: 40px; color: #64748b; font-size: 14px; padding-top: 20px; border-top: 1px solid #e2e8f0; }
          .evidence { color: #475569; line-height: 1.6; }
          @media print { body { padding: 0; background: white; } .container { box-shadow: none; padding: 20px; } }
        </style>
      </head>
      <body onload="window.print()">
        <div class="container">
        <h1>${currentView === 'cdm' ? 'CDM Grid' : 'Differential Diagnosis'}</h1>
        <h2>Session: ${sessionId} | Exported: ${new Date().toLocaleString()}</h2>
    `;
    if (currentView === 'cdm') {
      content += `<table><thead><tr><th style="width: 10%">Rank</th><th style="width: 25%">Diagnosis</th><th style="width: 32.5%">Supporting Evidence</th><th style="width: 32.5%">Opposing Evidence</th></tr></thead><tbody>`;
      allRows.forEach(row => {
        const diagnoses = (row.diagnoses || []).map(d => `${d.text} (${d.votes > 0 ? '+' : ''}${d.votes})`).join(', ');
        const supporting = (row.supporting || []).map(s => `${s.text} (${s.votes > 0 ? '+' : ''}${s.votes})`).join('; ');
        const opposing = (row.opposing || []).map(o => `${o.text} (${o.votes > 0 ? '+' : ''}${o.votes})`).join('; ');
        content += `<tr ${row.isCritical ? 'class="critical"' : ''}><td><span class="rank">${row.displayOrder}</span></td><td><div class="diagnosis">${diagnoses || 'None'}</div></td><td><div class="evidence">${supporting || 'None'}</div></td><td><div class="evidence">${opposing || 'None'}</div></td></tr>`;
      });
      content += '</tbody></table>';
    } else {
      content += `<table><thead><tr><th style="width: 20%">Etiological Class</th><th style="width: 20%">Diseases</th><th style="width: 30%">Supporting Evidence</th><th style="width: 30%">Opposing Evidence</th></tr></thead><tbody>`;
      sortedDifferentialRows.forEach(row => {
        const etiClass = (row.etiologicalClass || []).map(e => `${e.text} (${e.votes > 0 ? '+' : ''}${e.votes})`).join(', ');
        const diseases = (row.diseases || []).map(d => `${d.text} (${d.votes > 0 ? '+' : ''}${d.votes})`).join('; ');
        const supporting = (row.supporting || []).map(s => `${s.text} (${s.votes > 0 ? '+' : ''}${s.votes})`).join('; ');
        const opposing = (row.opposing || []).map(o => `${o.text} (${o.votes > 0 ? '+' : ''}${o.votes})`).join('; ');
        content += `<tr><td><strong>${etiClass || 'None'}</strong></td><td><div class="evidence">${diseases || 'None'}</div></td><td><div class="evidence">${supporting || 'None'}</div></td><td><div class="evidence">${opposing || 'None'}</div></td></tr>`;
      });
      content += '</tbody></table>';
    }
    content += `<div class="footer">Created by ZH (C2028)</div></div></body></html>`;
    const doc = printWindow.document; doc.open(); doc.write(content); doc.close();
  };

  const getItemStyle = (votes, maxVotes, minVotes, isGlobal = false) => {
    if (votes === 0) return { backgroundColor: 'rgba(255, 255, 255, 1)' };
    if (votes > 0) {
      const scale = isGlobal && maxVotes > 0 ? votes / maxVotes : Math.min(votes / 5, 1);
      const opacity = 0.3 + (scale * 0.7);
      return { backgroundColor: `rgba(34, 197, 94, ${opacity})` };
    }
    const scale = isGlobal && minVotes < 0 ? votes / minVotes : Math.min(Math.abs(votes) / 5, 1);
    const opacity = 0.3 + (scale * 0.7);
    return { backgroundColor: `rgba(239, 68, 68, ${opacity})` };
  };

  const ItemCard = ({ item, onVote, onDelete, canDelete, maxVotes, minVotes, isGlobal, isSmall = false }) => {
    const style = getItemStyle(item.votes || 0, maxVotes, minVotes, isGlobal);
    const userVoteAmt = (item.voters && item.voters[userName]) || 0;
    const upBtnClass = 'p-0.5 sm:p-1 rounded hover:bg-gray-100 transition-all';
    const downBtnClass = 'p-0.5 sm:p-1 rounded hover:bg-gray-100 transition-all';

    if (isSmall) {
      return (
        <div className="p-2 rounded-lg border border-gray-200 mb-1.5 transition-all duration-300" style={style}>
          <div className="flex justify-between items-start gap-1.5 mb-1">
            <p className="text-gray-900 flex-1 text-xs break-words">{item.text}</p>
            {canDelete && <button onClick={onDelete} className="text-red-500 hover:text-red-700 flex-shrink-0"><Trash2 className="w-3 h-3" /></button>}
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-600">{item.addedBy}</span>
            <div className="flex items-center gap-1.5">
              <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onVote(1); }} className={upBtnClass} title={`Upvote (+1). Your votes on this: ${userVoteAmt}`}>
                <ThumbsUp className="w-3 h-3" />
              </button>
              <span className="text-xs font-semibold min-w-[1.5rem] text-center text-gray-900">
                {item.votes || 0}
              </span>
              <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onVote(-1); }} className={downBtnClass} title={`Downvote (-1). Your votes on this: ${userVoteAmt}`}>
                <ThumbsDown className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="p-3 rounded-lg border border-gray-200 mb-2 transition-all duration-300" style={style}>
        <div className="flex justify-between items-start gap-2 mb-2">
          <p className="text-gray-900 flex-1 text-sm break-words">{item.text}</p>
          {canDelete && <button onClick={onDelete} className="text-red-500 hover:text-red-700 flex-shrink-0"><Trash2 className="w-4 h-4" /></button>}
        </div>
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-600">{item.addedBy}</span>
          <div className="flex items-center gap-2">
            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onVote(1); }} className={upBtnClass} title={`Upvote (+1). Your votes on this: ${userVoteAmt}`}>
              <ThumbsUp className="w-3.5 h-3.5" />
            </button>
            <span className="text-sm font-semibold min-w-[2rem] text-center text-gray-900">
              {item.votes || 0}
            </span>
            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onVote(-1); }} className={downBtnClass} title={`Downvote (-1). Your votes on this: ${userVoteAmt}`}>
              <ThumbsDown className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const InputField = memo(({ placeholder, onSubmit, onFocusChange }) => {
    const inputRef = useRef(null);
    const handleFocus = () => onFocusChange && onFocusChange(true);
    const handleBlur = () => onFocusChange && onFocusChange(false);
    const handleKeyPress = (e) => {
      if (e.key === 'Enter' && e.target.value.trim()) {
        onSubmit(e.target.value);
        e.target.value = '';
      }
    };
    return (
      <input 
        ref={inputRef}
        type="text" 
        placeholder={placeholder}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 outline-none"
        onKeyPress={handleKeyPress}
      />
    );
  });

  // Sort CDM rows by the most-upvoted diagnosis in that row
  const rowTopDxScore = (row) => Math.max(0, ...(row.diagnoses || []).map(d => d.votes || 0));
  const sortedRows = [...rows].sort((a, b) => rowTopDxScore(b) - rowTopDxScore(a));

  const sortedDifferentialRows = [...differentialRows].sort((a, b) => {
    const aMaxDiseaseVotes = Math.max(...(a.diseases || []).map(d => d.votes || 0), 0);
    const bMaxDiseaseVotes = Math.max(...(b.diseases || []).map(d => d.votes || 0), 0);
    return bMaxDiseaseVotes - aMaxDiseaseVotes;
  });

  const allRows = [
    ...sortedRows.map((r, i) => ({ ...r, displayOrder: String(i + 1) })),
    ...criticalRows.map((r, i) => ({ ...r, displayOrder: `Critical Dx ${i + 1}` }))
  ];
  
  const allDiagnoses = allRows.flatMap(r => r.diagnoses || []);
  const globalMaxVotes = allDiagnoses.length > 0 ? Math.max(...allDiagnoses.map(d => d.votes || 0).filter(v => v > 0), 1) : 1;
  const globalMinVotes = allDiagnoses.length > 0 ? Math.min(...allDiagnoses.map(d => d.votes || 0).filter(v => v < 0), -1) : -1;

  useEffect(() => {
    const newPositions = {};
    allRows.forEach((row, index) => { newPositions[row.id] = index; });
    sortedDifferentialRows.forEach((row, index) => { newPositions[`diff_${row.id}`] = index; });
    rowPositionsRef.current = newPositions;
  }, [allRows, sortedDifferentialRows]);

  const getRowStyle = (rowId, currentIndex, isDifferential = false) => {
    const key = isDifferential ? `diff_${rowId}` : rowId;
    const previousIndex = rowPositionsRef.current[key];
    if (previousIndex !== undefined && previousIndex !== currentIndex) {
      const delta = (previousIndex - currentIndex) * 100;
      return { animation: `slideFromPosition 0.5s ease-out`, '--slide-distance': `${delta}px` };
    }
    return {};
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <style>{`
          @keyframes slideFromPosition {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
        <div className="bg-white rounded-3xl shadow-xl p-8 sm:p-12 max-w-md w-full">
          <div className="flex items-center justify-center mb-8">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <Users className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl font-light text-gray-900 mb-2 text-center">CDM Grid</h1>
          <p className="text-gray-500 mb-8 text-center text-sm">Collaborative clinical decision making</p>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1.5 block">Your Name</label>
              <input type="text" value={userName} onChange={(e) => setUserName(e.target.value)} placeholder="Enter your name" className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-400 outline-none" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1.5 block">Session ID</label>
              <input type="text" value={sessionId} onChange={(e) => setSessionId(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && userName.trim() && sessionId.trim() && setIsLoggedIn(true)} placeholder="Create or join a session" className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-400 outline-none" />
            </div>
            <button onClick={() => userName.trim() && sessionId.trim() && setIsLoggedIn(true)} className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3.5 rounded-xl hover:shadow-lg transition font-medium">Join Session</button>
          </div>
          <p className="text-xs text-gray-400 mt-6 text-center">Use the same Session ID to collaborate in real-time</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-2 sm:p-4 md:p-8">
      <style>{`
        @keyframes slideFromPosition {
          from { transform: translateY(var(--slide-distance)); }
          to { transform: translateY(0); }
        }
      `}</style>
      <div className="max-w-[1900px] mx-auto">
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="w-full sm:w-auto">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-light text-gray-900">{currentView === 'cdm' ? 'CDM Grid' : 'Differential Diagnosis'}</h1>
              <div className="flex items-center gap-2 sm:gap-3 mt-2 text-xs sm:text-sm">
                <span className="text-gray-500"><span className="font-medium text-gray-700">{userName}</span></span>
                <span className="text-gray-300">•</span>
                <span className="text-gray-500 flex items-center gap-1 sm:gap-1.5"><Users className="w-3 h-3 sm:w-3.5 sm:h-3.5" />Session: <span className="font-medium text-indigo-600">{sessionId}</span></span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center w-full sm:w-auto">
              <div className="bg-gray-100 rounded-xl p-1 flex">
                <button onClick={() => setCurrentView('cdm')} className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition ${currentView === 'cdm' ? 'bg-white text-blue-600 shadow' : 'text-gray-600'}`}>CDM</button>
                <button onClick={() => setCurrentView('differential')} className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition ${currentView === 'differential' ? 'bg-white text-purple-600 shadow' : 'text-gray-600'}`}>Differential</button>
              </div>
              <div className="flex gap-2">
                <button onClick={exportToPDF} className="flex-1 sm:flex-none bg-green-500 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-green-600 transition flex items-center justify-center gap-1.5 text-xs sm:text-sm"><Download className="w-3 h-3 sm:w-4 sm:h-4" />Export</button>
                {currentView === 'cdm' ? (
                  <div className="relative flex-1 sm:flex-none" ref={dropdownRef}>
                    <button onClick={() => setShowAddRowDropdown(!showAddRowDropdown)} className="w-full bg-blue-500 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-blue-600 transition flex items-center justify-center gap-1.5 text-xs sm:text-sm"><Plus className="w-3 h-3 sm:w-4 sm:h-4" />Add Row<ChevronDown className="w-3 h-3 sm:w-4 sm:h-4" /></button>
                    {showAddRowDropdown && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border py-1 z-50">
                        <button onClick={() => { addRow(); setShowAddRowDropdown(false); }} className="w-full px-4 py-2 text-left text-sm hover:bg-blue-50 flex items-center gap-2"><Plus className="w-4 h-4 text-blue-500" />Add Regular Row</button>
                        <button onClick={() => { addCriticalRow(); setShowAddRowDropdown(false); }} className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 flex items-center gap-2"><Plus className="w-4 h-4 text-red-500" />Add Critical Dx</button>
                      </div>
                    )}
                  </div>
                ) : (
                  <button onClick={addDifferentialRow} className="flex-1 sm:flex-none bg-blue-500 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-blue-600 transition flex items-center justify-center gap-1.5 text-xs sm:text-sm"><Plus className="w-3 h-3 sm:w-4 sm:h-4" />Add Row</button>
                )}
              </div>
            </div>
          </div>
        </div>

        {currentView === 'cdm' ? (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto -webkit-overflow-scrolling-touch">
              <table className="w-full min-w-[800px]">
                <thead>
                  <tr className="bg-gradient-to-r from-blue-500 to-purple-600 text-white">
                    <th className="p-3 sm:p-4 text-left text-xs sm:text-sm font-medium w-[8%]">Likelihood</th>
                    <th className="p-3 sm:p-4 text-left text-xs sm:text-sm font-medium w-[25%]">Diagnosis</th>
                    <th className="p-3 sm:p-4 text-left text-xs sm:text-sm font-medium w-[33%]">Supporting</th>
                    <th className="p-3 sm:p-4 text-left text-xs sm:text-sm font-medium w-[34%]">Opposing</th>
                  </tr>
                </thead>
              <tbody>
                {allRows.map((row, idx) => (
                  <tr key={row.id} className={`${row.isCritical ? 'bg-red-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`} style={getRowStyle(row.id, idx)}>
                    <td className="border-t p-3 sm:p-4">
                      <span className={`font-semibold text-lg sm:text-xl ${row.isCritical ? 'text-red-600' : 'text-blue-600'}`}>{row.displayOrder}</span>
                    </td>
                    <td className="border-t border-l p-3 sm:p-4">
                      <div className="mb-3 max-h-96 overflow-y-auto">
                        {(row.diagnoses || []).map(d => (
                          <ItemCard
                            key={d.id}
                            item={d}
                            onVote={(v) => voteItem(row.id, 'diagnoses', d.id, v, row.isCritical)}
                            onDelete={() => deleteItem(row.id, 'diagnoses', d.id, row.isCritical)}
                            canDelete={d.addedBy === userName}
                            maxVotes={globalMaxVotes}
                            minVotes={globalMinVotes}
                            isGlobal={true}
                          />
                        ))}
                      </div>
                      {(row.diagnoses || []).length === 0 && (
                        <InputField
                          key={`input-diag-${row.id}`}
                          placeholder="Add diagnosis..."
                          onSubmit={(text) => addItem(row.id, 'diagnoses', text, row.isCritical)}
                          onFocusChange={handleFocusChange}
                        />
                      )}
                    </td>
                    <td className="border-t border-l p-3 sm:p-4">
                      <div className="mb-3 max-h-96 overflow-y-auto">
                        {(row.supporting || []).map(s => (
                          <ItemCard
                            key={s.id}
                            item={s}
                            onVote={(v) => voteItem(row.id, 'supporting', s.id, v, row.isCritical)}
                            onDelete={() => deleteItem(row.id, 'supporting', s.id, row.isCritical)}
                            canDelete={s.addedBy === userName}
                            maxVotes={globalMaxVotes}
                            minVotes={globalMinVotes}
                            isGlobal={true}
                            isSmall={true}
                          />
                        ))}
                      </div>
                      <InputField
                        key={`input-supp-${row.id}`}
                        placeholder="Add supporting..."
                        onSubmit={(text) => addItem(row.id, 'supporting', text, row.isCritical)}
                        onFocusChange={handleFocusChange}
                      />
                    </td>
                    <td className="border-t border-l p-3 sm:p-4">
                      <div className="mb-3 max-h-96 overflow-y-auto">
                        {(row.opposing || []).map(o => (
                          <ItemCard
                            key={o.id}
                            item={o}
                            onVote={(v) => voteItem(row.id, 'opposing', o.id, v, row.isCritical)}
                            onDelete={() => deleteItem(row.id, 'opposing', o.id, row.isCritical)}
                            canDelete={o.addedBy === userName}
                            maxVotes={globalMaxVotes}
                            minVotes={globalMinVotes}
                            isGlobal={true}
                            isSmall={true}
                          />
                        ))}
                      </div>
                      <InputField
                        key={`input-opp-${row.id}`}
                        placeholder="Add opposing..."
                        onSubmit={(text) => addItem(row.id, 'opposing', text, row.isCritical)}
                        onFocusChange={handleFocusChange}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto -webkit-overflow-scrolling-touch">
              <table className="w-full min-w-[800px]">
              <thead>
                <tr className="bg-gradient-to-r from-purple-500 to-pink-600 text-white">
                  <th className="p-3 sm:p-4 text-left text-xs sm:text-sm font-medium w=[20%]">Etiological Class</th>
                  <th className="p-3 sm:p-4 text-left text-xs sm:text-sm font-medium w=[20%]">Diseases</th>
                  <th className="p-3 sm:p-4 text-left text-xs sm:text-sm font-medium w=[30%]">Supporting</th>
                  <th className="p-3 sm:p-4 text-left text-xs sm:text-sm font-medium w=[30%]">Opposing</th>
                </tr>
              </thead>
              <tbody>
                {sortedDifferentialRows.map((row, idx) => (
                  <tr key={row.id} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`} style={getRowStyle(row.id, idx, true)}>
                    <td className="border-t p-3 sm:p-4">
                      <div className="mb-3">
                        {(row.etiologicalClass || []).map(e => (
                          <ItemCard
                            key={e.id}
                            item={e}
                            onVote={(v) => voteDifferentialItem(row.id, 'etiologicalClass', e.id, v)}
                            onDelete={() => deleteDifferentialItem(row.id, 'etiologicalClass', e.id)}
                            canDelete={e.addedBy === userName}
                            maxVotes={5}
                            minVotes={-5}
                            isGlobal={false}
                          />
                        ))}
                      </div>
                      {(row.etiologicalClass || []).length === 0 && (
                        <InputField
                          key={`input-etio-${row.id}`}
                          placeholder="Add etiological class..."
                          onSubmit={(text) => addDifferentialItem(row.id, 'etiologicalClass', text)}
                          onFocusChange={handleFocusChange}
                        />
                      )}
                    </td>
                    <td className="border-t border-l p-3 sm:p-4">
                      <div className="mb-3 max-h-96 overflow-y-auto">
                        {(row.diseases || []).map(d => (
                          <ItemCard
                            key={d.id}
                            item={d}
                            onVote={(v) => voteDifferentialItem(row.id, 'diseases', d.id, v)}
                            onDelete={() => deleteDifferentialItem(row.id, 'diseases', d.id)}
                            canDelete={d.addedBy === userName}
                            maxVotes={5}
                            minVotes={-5}
                            isGlobal={false}
                          />
                        ))}
                      </div>
                      <InputField
                        key={`input-dis-${row.id}`}
                        placeholder="Add disease..."
                        onSubmit={(text) => addDifferentialItem(row.id, 'diseases', text)}
                        onFocusChange={handleFocusChange}
                      />
                    </td>
                    <td className="border-t border-l p-3 sm:p-4">
                      <div className="mb-3 max-h-96 overflow-y-auto">
                        {(row.supporting || []).map(s => (
                          <ItemCard
                            key={s.id}
                            item={s}
                            onVote={(v) => voteDifferentialItem(row.id, 'supporting', s.id, v)}
                            onDelete={() => deleteDifferentialItem(row.id, 'supporting', s.id)}
                            canDelete={s.addedBy === userName}
                            maxVotes={5}
                            minVotes={-5}
                            isGlobal={false}
                            isSmall={true}
                          />
                        ))}
                      </div>
                      <InputField
                        key={`input-diff-supp-${row.id}`}
                        placeholder="Add supporting..."
                        onSubmit={(text) => addDifferentialItem(row.id, 'supporting', text)}
                        onFocusChange={handleFocusChange}
                      />
                    </td>
                    <td className="border-t border-l p-3 sm:p-4">
                      <div className="mb-3 max-h-96 overflow-y-auto">
                        {(row.opposing || []).map(o => (
                          <ItemCard
                            key={o.id}
                            item={o}
                            onVote={(v) => voteDifferentialItem(row.id, 'opposing', o.id, v)}
                            onDelete={() => deleteDifferentialItem(row.id, 'opposing', o.id)}
                            canDelete={o.addedBy === userName}
                            maxVotes={5}
                            minVotes={-5}
                            isGlobal={false}
                            isSmall={true}
                          />
                        ))}
                      </div>
                      <InputField
                        key={`input-diff-opp-${row.id}`}
                        placeholder="Add opposing..."
                        onSubmit={(text) => addDifferentialItem(row.id, 'opposing', text)}
                        onFocusChange={handleFocusChange}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>
      
      <footer className="mt-6 sm:mt-8 text-center text-gray-500 text-xs sm:text-sm pb-4">
        <p>Created by ZH (C2028)</p>
      </footer>
    </div>
  );
}